import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { Navigate } from 'react-router-dom';
import { useAuth } from './useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { useImpersonation } from '../hooks/useImpersonation';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
}

export const ProtectedRoute = ({ children, requiredRoles }: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading, user, login } = useAuth();
  // Effective role — honors the superadmin "View as role" preview so route
  // access matches what the previewed role would actually get (RBAC.md).
  const { role: effectiveRole } = usePermissions();
  const { isImpersonating, impersonatingAs } = useImpersonation();
  const { inProgress } = useMsal();

  // Wait for MSAL to finish processing redirects AND for auth state to settle
  if (isLoading || inProgress !== InteractionStatus.None) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={32} sx={{ color: '#3E4A3E' }} />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.status === 'pending') {
    return <Navigate to="/access-pending" replace />;
  }

  if (user?.status === 'disabled') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Paper sx={{ p: 4, maxWidth: 420, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>Access Denied</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Your account has been disabled. Please contact your administrator.
          </Typography>
          <Button variant="outlined" onClick={() => login()}>Try Another Account</Button>
        </Paper>
      </Box>
    );
  }

  if (requiredRoles && requiredRoles.length > 0 && user?.role) {
    // Superadmin bypasses all role requirements — unless previewing a lower
    // role, in which case the preview should be blocked exactly like that role.
    if (effectiveRole !== 'superadmin' && !requiredRoles.includes(effectiveRole)) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <Paper sx={{ p: 4, maxWidth: 420, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>Unauthorized</Typography>
            <Typography color="text.secondary">
              {isImpersonating
                ? `The "${impersonatingAs}" role cannot access this page. Exit the role preview (avatar menu) to return.`
                : 'You do not have permission to access this page.'}
            </Typography>
          </Paper>
        </Box>
      );
    }
  }

  return <>{children}</>;
};
